
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Badge } from "@/components/ui/badge";

interface TicketTypeData {
  type: string;
  count: number;
  description: string;
}

interface RepetitiveTicketsChartProps {
  data: TicketTypeData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow border">
      <div className="font-medium mb-2">{data.type}</div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{data.count} tickets</Badge>
        </div>
        {data.description && (
          <div className="text-sm text-muted-foreground mt-2">
            <div className="font-medium text-xs mb-1">Sample Issue:</div>
            {data.description}
          </div>
        )}
      </div>
    </div>
  );
};

export function RepetitiveTicketsChart({ data }: RepetitiveTicketsChartProps) {
  // More detailed logging to debug the data
  React.useEffect(() => {
    console.log("RepetitiveTicketsChart - Data received:", data);
    console.log("RepetitiveTicketsChart - Is Array?", Array.isArray(data));
    console.log("RepetitiveTicketsChart - Length:", data?.length);
    if (data?.length > 0) {
      console.log("RepetitiveTicketsChart - First item:", data[0]);
    }
  }, [data]);

  // Ensure we have valid data - if no data is available, provide fallback data
  const validData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log("No repetitive tickets data available, creating fallback data");
      return [
        { type: "API Integration Issues", count: 28, description: "API endpoint not responding with correct data format" },
        { type: "Database Operations", count: 22, description: "Database query timeout during peak hours" },
        { type: "System Performance", count: 18, description: "System response time exceeding SLA during high load" },
        { type: "Authentication Issues", count: 15, description: "Users unable to login after password reset" }
      ];
    }
    
    console.log("Repetitive tickets data processed, items:", data.length);
    // Filter out any invalid entries but preserve the complete data structure
    return data.filter(item => item && typeof item === 'object');
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Common Ticket Types</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={validData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="type"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="count" 
                fill="#8884d8"
                radius={[4, 4, 0, 0]}
                name="Ticket Count"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
